import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { CoreService } from './core.service'
import { NotificationService } from './notification.service'
import { Category, Product } from 'depoto-core/src/entities'
import { roundNum, roundEnovaDown } from '../utils'

type HsApiEndpoint = 'addresses' | 'discounts' | 'documents' | 'products'
type HsAddress = {
  name: string
  customerCode: string
  street: string
  houseNumber: string
  city: string
  zip: string
  phone: string
  country: string
}

@Injectable({
  providedIn: 'root',
})
export class HsApiService {
  customerCode: string

  constructor(
    private readonly core: CoreService,
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService,
  ) {
    this.customerCode = this.core.services.user.user?.company?.externalId
  }

  private getSyncApiUrl(endpoint: HsApiEndpoint) {
    const server = this.core.services.oauth.session.clientType
    const country = '?country=' + (this.core.services.user.user?.company?.country || 'CZ')
    if (server === 'prod-hs') {
      return `https://sync-api.evidentist.cz/api/${endpoint}${country}`
    } else if (server === 'stage-hs' || server === 'dev') {
      return `https://sync-api.evidentist.cz.tomatomstage.cz/api/${endpoint}${country}`
    }
  }

  private async fetchHsApi(url: string, initArgs?: any) {
    const token = this.core.services.oauth.getAccessToken()
    const headers = new Headers()
    // if (initArgs?.method !== 'POST') {
    // todo, createAddress (post addresses) returns 405 with auth header. remove condition after server fix
    headers.append('Authorization', `Bearer ${token}`)
    // }
    return await fetch(url, { ...initArgs, headers }).then(resp => resp.json())
  }

  async getHsTemplate(externalId: string, requireSellable = true) {
    if (!externalId) {
      return
    }
    const url = `${this.getSyncApiUrl('products')}&hsid=${externalId}`
    try {
      const response = await this.fetchHsApi(url)
      const item = response?.items?.length === 1 ? response.items[0] : undefined
      if (!item) {
        return undefined
      }
      if (requireSellable && !this.isSellable(item)) {
        return undefined
      }
      return item
    } catch (e) {
      console.log('HsApi:getHsTemplate', e)
    }
  }

  async searchForHsTemplate(searchPhrase: string, filteredCategories?: Category[], isSellable?: boolean) {
    let categoriesArg = ''
    if (filteredCategories?.length > 1) {
      let i = 0
      for (const category of filteredCategories) {
        categoriesArg = categoriesArg + '&categories[' + i + ']=' + category.externalId
        i++
      }
    } else if (filteredCategories?.length === 1) {
      categoriesArg = categoriesArg + '&categories[0]=' + filteredCategories[0].externalId
    }

    try {
      const url = `${this.getSyncApiUrl('products')}${categoriesArg}&fulltext=${searchPhrase}${isSellable ? (isSellable !== undefined ? `&sellable=${isSellable ? 'true' : 'false'}` : '') : ''}`
      const response = await this.fetchHsApi(url)
      if (isSellable !== undefined) {
        const sellableTemplates = response?.items?.filter((t: any) => this.isSellable(t))
        response.items = sellableTemplates
      }
      return response
    } catch (e) {
      console.log('HsApi:searchForHsTemplate', e)
    }
  }

  async getHsDiscounts(externalId: string, countryCode?: string) {
    if (!externalId) {
      return
    }
    try {
      const url = `${this.getSyncApiUrl('discounts')}&productHsId=${externalId}&customerCode=${this.customerCode}${countryCode?.length ? `&country=${countryCode}` : ''}`
      // const url = `https://sync-api.evidentist.cz.tomatomstage.cz/api/discounts${this.getCountryQueryParam()}&productHsId=${externalId}&customerCode=${this.customerCode}`
      return await this.fetchHsApi(url)
    } catch (e) {
      console.log('HsApi:getHsDiscounts', e)
    }
  }

  async getHsDocuments(hsOrderId: number | string) {
    if (!hsOrderId) {
      return
    }
    try {
      const url = `${this.getSyncApiUrl('documents')}&orderHsId=${hsOrderId}`
      return await this.fetchHsApi(url)
    } catch (e) {
      console.log('HsApi:getHsDocuments', e)
    }
  }

  async getHsDocument(url: string) {
    // todo test method
    if (!url) {
      return
    }
    try {
      return await this.fetchHsApi(url, {
        method: 'POST',
      })
    } catch (e) {
      console.log('HsApi:getHsDocuments', e)
    }
  }

  async getHsAddresses() {
    try {
      const url = `${this.getSyncApiUrl('addresses')}&customerCode=${this.customerCode}`
      return await this.fetchHsApi(url)
    } catch (e) {
      console.log('HsApi:getHsAddresses', e)
    }
  }

  async createHsAddress(address: HsAddress) {
    try {
      return await this.fetchHsApi(this.getSyncApiUrl('addresses'), {
        method: 'POST',
        body: JSON.stringify(address),
      })
    } catch (e) {
      console.log('HsApi:createHsAddress', e)
    }
  }

  async getQuantityHsForProduct(product: Product | any) {
    try {
      const template = await this.getHsTemplate(product.externalId)
      const vatRateHs = template?.vatRate || 0
      product.quantityHs = !template ? 'notConnected' : template?.sellable ? template?.quantityAvailable : 'nonSellable'
      if (!product.customData) {
        product.customData = { vatHs: vatRateHs }
      } else {
        product.customData.vatHs = vatRateHs
      }
      this.overrideVatIfZero(product, vatRateHs)

      return product
    } catch (e) {
      console.log('error setting quantityHs', e, product)
    }
  }

  /**
   * Resolves the VAT entity matching the saved HS vat rate (customData.vatHs).
   * Used for already-saved purchase orders so calculations always use the stored
   * vatHs rate instead of the current product.vat. Falls back to the provided vat
   * when no matching rate is found.
   */
  resolveVat(vatHs: number | string | undefined | null, fallbackVat?: any) {
    if (vatHs !== undefined && vatHs !== null && `${vatHs}`.length > 0) {
      const matchingVat = this.core.lists.vats.find(v => Number(v.percent) === Number(vatHs))
      if (matchingVat) {
        return matchingVat
      }
    }
    return fallbackVat
  }

  /**
   * Coefficient for the saved HS vat rate, used as `priceWithoutVat / coefficient = priceWithVat`.
   * Prefers the matching VAT entity's coefficient; if that rate is not in the company VAT list it
   * derives it directly from the rate (1 / (1 + rate/100)), so saved orders never silently fall
   * back to product.vat.
   */
  getVatHsCoefficient(vatHs: number | string | undefined | null, fallbackVat?: any): number {
    const resolved = this.resolveVat(vatHs)
    if (resolved?.coefficient) {
      return resolved.coefficient
    }
    const rate = Number(vatHs)
    if (vatHs !== undefined && vatHs !== null && `${vatHs}`.length > 0 && !isNaN(rate)) {
      return 1 / (1 + rate / 100)
    }
    return fallbackVat?.coefficient || 1
  }

  private overrideVatIfZero(product: any, vatRateHs: number) {
    if (product.vat?.percent === 0 && vatRateHs > 0) {
      const matchingVat = this.core.lists.vats.find(v => v.percent === vatRateHs)
      if (matchingVat) {
        product.vat = matchingVat
        if (product.purchasePrice !== undefined) {
          product.purchasePriceWithVat = product.purchasePrice / (product.vat?.coefficient || 1)
        }
        if (product.purchasePriceAll !== undefined) {
          product.purchasePriceAllWithVat =
            (product.purchasePriceWithVat || product.purchasePrice) * (product.quantity || 1)
        }
        if (product.purchasePriceWithDiscount !== undefined) {
          product.purchasePriceWithVatAndDiscount = product.purchasePriceWithDiscount / (product.vat?.coefficient || 1)
        }
        if (product.purchasePriceAllWithDiscount !== undefined) {
          product.purchasePriceAllWithVatAndDiscount =
            (product.purchasePriceWithVatAndDiscount || product.purchasePriceWithDiscount) * (product.quantity || 1)
        }
      }
    }
  }

  async getDiscountsForProduct(product: Product | any) {
    const productDiscounts = await this.getHsDiscounts(product.externalId)
    if (!productDiscounts || productDiscounts?.error || !productDiscounts?.bestDiscount?.sellable) {
      // console.log('error fetching discounts', productDiscounts)
      // this.notificationService.error(this.translateService.instant('purchaseOrder.error.product.notSellable'))
      return
    }
    product.discounts = productDiscounts
    product.discount = productDiscounts.bestDiscount?.discountPercent || 0
    if (product.discount > 0) {
      product.purchasePrice = productDiscounts.bestDiscount?.purchasePrice
      product.purchasePriceWithVat = roundNum(product.purchasePrice / (product.vat?.coefficient || 1))
      product.purchasePriceAll = roundNum(product.purchasePrice * (product.quantity || 1))
      product.purchasePriceAllWithVat = roundNum(product.purchasePriceAll / (product.vat?.coefficient || 1))

      product.purchasePriceWithDiscount = roundEnovaDown(product.purchasePrice * (1 - product.discount / 100))
      product.purchasePriceWithVatAndDiscount = roundNum(
        product.purchasePriceWithDiscount / (product.vat?.coefficient || 1),
      )

      product.purchasePriceAllWithDiscount = roundNum(product.purchasePriceWithDiscount * (product.quantity || 1))
      product.purchasePriceAllWithVatAndDiscount = roundNum(
        product.purchasePriceAllWithDiscount / (product.vat?.coefficient || 1),
      )
    }
    return product
  }

  isSellable(hsTemplate: any): boolean {
    const country = this.core.services.user.user?.company?.country || 'CZ'
    return hsTemplate?.locales?.[country]?.sellable
  }
}
